import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Users, CreditCard, DollarSign, Hourglass, Loader2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("fr-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const fmtMoney = (cents, currency) =>
  ((cents || 0) / 100).toLocaleString("fr-CA", {
    style: "currency",
    currency: (currency || "cad").toUpperCase(),
  });

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("cpmc_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [inscriptions, setInscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/admin/overview`, { headers }),
      axios.get(`${API}/admin/inscriptions`, { headers }),
      axios.get(`${API}/admin/transactions`, { headers }),
    ])
      .then(([o, i, t]) => {
        setOverview(o.data);
        setInscriptions(i.data.inscriptions);
        setTransactions(t.data.transactions);
      })
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          sessionStorage.removeItem("cpmc_admin_token");
          setToken(null);
          toast.error("Session expirée — reconnectez-vous");
        } else {
          toast.error("Erreur de chargement des données");
        }
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem("cpmc_admin_token", r.data.token);
      setToken(r.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("cpmc_admin_token");
    setToken(null);
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/inscriptions/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "inscriptions-cpmc.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Liste téléchargée");
    } catch {
      toast.error("Export impossible");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-5 text-cream">
        <form
          onSubmit={login}
          data-testid="cpmc-admin-login-form"
          className="w-full max-w-md rounded-3xl border border-cream/10 bg-cream/[0.05] p-10 backdrop-blur"
        >
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-sun">
            <Lock className="h-6 w-6 text-ink" />
          </div>
          <h1 className="text-center font-display text-2xl font-bold">Administration CPMC</h1>
          <p className="mb-8 mt-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
            Accès réservé
          </p>
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
            Courriel
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="cpmc-admin-input-email"
            className="mb-5 w-full rounded-xl border border-ink/15 bg-cream px-4 py-3.5 text-sm text-ink outline-none focus:border-teal focus:ring-2 focus:ring-teal/20"
            placeholder="info@cpmc-quebec.ca"
          />
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
            Mot de passe
          </label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="cpmc-admin-input-password"
            className="mb-8 w-full rounded-xl border border-ink/15 bg-cream px-4 py-3.5 text-sm text-ink outline-none focus:border-teal focus:ring-2 focus:ring-teal/20"
            placeholder="••••••••••••"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="cpmc-admin-btn-login"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-sun px-6 py-4 font-display font-bold text-ink transition-colors duration-300 hover:bg-teal hover:text-cream disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Se connecter"}
          </button>
          <a
            href="/"
            className="mt-6 block text-center font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40 hover:text-cream"
          >
            ← Retour au site
          </a>
        </form>
      </div>
    );
  }

  const stats = [
    { icon: Users, label: "Inscriptions", value: overview?.total_inscriptions ?? "…", testid: "cpmc-admin-stat-total" },
    { icon: CreditCard, label: "Payées", value: overview?.paid_count ?? "…", testid: "cpmc-admin-stat-paid" },
    { icon: Hourglass, label: "En attente", value: overview?.pending_count ?? "…", testid: "cpmc-admin-stat-pending" },
    {
      icon: DollarSign,
      label: "Revenus",
      value: overview ? overview.revenue_cad.toLocaleString("fr-CA", { style: "currency", currency: "CAD" }) : "…",
      testid: "cpmc-admin-stat-revenue",
    },
  ];

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="sticky top-0 z-40 border-b border-ink/10 bg-cream/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <div>
            <h1 className="font-display text-xl font-bold">Tableau des inscriptions</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">
              Formation CPMC — cohorte d'octobre 2026
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="hidden font-mono text-xs uppercase tracking-[0.2em] text-ink/60 hover:text-ink sm:inline"
            >
              Voir le site
            </a>
            <button
              onClick={logout}
              data-testid="cpmc-admin-btn-logout"
              className="flex items-center gap-2 rounded-full border-2 border-ink px-4 py-2 text-sm font-semibold transition-colors hover:bg-coral hover:border-coral hover:text-white"
            >
              <LogOut className="h-4 w-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              data-testid={s.testid}
              className="rounded-2xl border border-ink/10 bg-white/70 p-6 shadow-sm"
            >
              <s.icon className="mb-3 h-5 w-5 text-teal" />
              <p className="font-display text-2xl font-extrabold sm:text-3xl">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Inscriptions</h2>
          <button
            onClick={exportCsv}
            data-testid="cpmc-admin-btn-export"
            className="flex items-center gap-2 rounded-full border-2 border-ink px-4 py-2 text-sm font-semibold transition-colors duration-300 hover:border-teal hover:bg-teal hover:text-cream"
          >
            <Download className="h-4 w-4" />
            Exporter Excel/CSV
          </button>
        </div>
        <div data-testid="cpmc-admin-table-inscriptions" className="mb-12 overflow-x-auto rounded-2xl border border-ink/10 bg-white/70">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-ink/10 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">
                <th className="px-5 py-4">Nom</th>
                <th className="px-5 py-4">Courriel</th>
                <th className="px-5 py-4">Profession</th>
                <th className="px-5 py-4">Organisation</th>
                <th className="px-5 py-4">Promo</th>
                <th className="px-5 py-4">Paiement</th>
                <th className="px-5 py-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {inscriptions.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-ink/40">
                    Aucune inscription pour le moment
                  </td>
                </tr>
              )}
              {inscriptions.map((r) => (
                <tr key={r.id} className="border-b border-ink/5 last:border-0 hover:bg-mint/10">
                  <td className="px-5 py-4 font-semibold">{r.name}</td>
                  <td className="px-5 py-4">{r.email}</td>
                  <td className="px-5 py-4">{r.profession}</td>
                  <td className="px-5 py-4">{r.organization || "—"}</td>
                  <td className="px-5 py-4 font-mono text-xs">{r.promo_code || "—"}</td>
                  <td className="px-5 py-4">
                    {r.paid ? (
                      <span className="rounded-full bg-teal/15 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-teal-dark">
                        Payée
                      </span>
                    ) : (
                      <span className="rounded-full bg-sun/30 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-ink/70">
                        En attente
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-ink/60">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mb-4 font-display text-lg font-bold">Transactions Stripe</h2>
        <div data-testid="cpmc-admin-table-transactions" className="overflow-x-auto rounded-2xl border border-ink/10 bg-white/70">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-ink/10 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">
                <th className="px-5 py-4">Session</th>
                <th className="px-5 py-4">Montant</th>
                <th className="px-5 py-4">Statut</th>
                <th className="px-5 py-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-ink/40">
                    Aucune transaction pour le moment
                  </td>
                </tr>
              )}
              {transactions.map((t) => (
                <tr key={t.session_id} className="border-b border-ink/5 last:border-0 hover:bg-mint/10">
                  <td className="px-5 py-4 font-mono text-xs">{t.session_id?.slice(0, 24)}…</td>
                  <td className="px-5 py-4 font-semibold">{fmtMoney(t.amount, t.currency)}</td>
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest ${
                        t.payment_status === "paid"
                          ? "bg-teal/15 text-teal-dark"
                          : t.payment_status === "pending"
                            ? "bg-sun/30 text-ink/70"
                            : "bg-coral/15 text-coral"
                      }`}
                    >
                      {t.payment_status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-ink/60">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
